import React, { useState } from 'react'
import 'feather-icons'
import { withRouter } from 'react-router-dom'
import { Text } from 'rebass'
import styled from 'styled-components'
import Link from '../components/Link'
import Panel from '../components/Panel'
import TokenLogo from '../components/TokenLogo'
import PairList from '../components/PairList'
import Loader from '../components/LocalLoader'
import { AutoRow, RowBetween, RowFixed } from '../components/Row'
import Column, { AutoColumn } from '../components/Column'
import { ButtonNoBackground, ButtonInverse } from '../components/ButtonStyled'
import TxnList from '../components/TxnList'
import TokenChart from '../components/TokenChart'
import { BasicLink } from '../components/Link'
import { formattedNum, formattedPercent, getPoolLink, getSwapLink, localNumber } from '../utils'
import { useTokenData, useTokenTransactions, useTokenPairs } from '../contexts/TokenData'
import { TYPE } from '../Theme'
import { useColor } from '../hooks'
import CopyHelper from '../components/Copy'
import { useMedia } from 'react-use'
import { useDataForList } from '../contexts/PairData'
import { useEffect } from 'react'
import Warning from '../components/Warning'
import { usePathDismissed, useSavedTokens } from '../contexts/LocalStorage'
import { Hover, PageWrapper, ContentWrapper, StyledIcon } from '../components'
import { PlusCircle, Bookmark } from 'react-feather'
import FormattedName from '../components/FormattedName'
import { useListedTokens } from '../contexts/Application'
import ENERGISWAP_DEFAULT_LIST from '@energi/energiswap-default-token-list'
import getEnvironmentVar from '../utils/getEnvironmentVar'
import replaceSyntheticName from '../utils/replace-synthetic-name'
import replaceSymbolE from '../utils/replace-symbol-e'
import ScrollUp from '../components/ScrollUp'

const EXPLORER_URL = getEnvironmentVar('EXPLORER_URL')
const WNRG_ADDRESS = getEnvironmentVar('WNRG_ADDRESS')

const DashboardWrapper = styled.div`
  width: 100%;
`

const PanelWrapper = styled.div`
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: max-content;
  gap: 6px;
  display: inline-grid;
  width: 100%;
  align-items: start;
  @media screen and (max-width: 1024px) {
    grid-template-columns: 1fr;
    align-items: stretch;
    > * {
      grid-column: 1 / 4;
    }

    > * {
      &:first-child {
        width: 100%;
      }
    }
  }
`

const TokenDetailsLayout = styled.div`
  display: inline-grid;
  width: 100%;
  grid-template-columns: auto auto auto 1fr;
  column-gap: 30px;
  align-items: start;

  &:last-child {
    align-items: center;
    justify-items: end;
  }
  @media screen and (max-width: 1024px) {
    grid-template-columns: 1fr;
    align-items: stretch;
    > * {
      grid-column: 1 / 4;
      margin-bottom: 1rem;
    }

    &:last-child {
      align-items: start;
      justify-items: start;
    }
  }
`

const WarningGrouping = styled.div`
  opacity: ${({ disabled }) => disabled && '0.4'};
  pointer-events: ${({ disabled }) => disabled && 'none'};
`

function TokenPage({ address, history }) {
  let addLiquidity, tokensDesc, tokenInformation

  const {
    id,
    name,
    symbol,
    priceUSD,
    oneDayVolumeUSD,
    totalLiquidityUSD,
    volumeChangeUSD,
    oneDayVolumeUT,
    volumeChangeUT,
    priceChangeUSD,
    liquidityChangeUSD,
    oneDayTxns,
    txnChange,
  } = useTokenData(address)

  useEffect(() => {
    document.querySelector('body').scrollTo(0, 0)
  }, [])

  const isEnergiCoin = address.toLowerCase() === WNRG_ADDRESS.toLowerCase()

  // detect color from token
  const backgroundColor = useColor(id, symbol)

  const allPairs = useTokenPairs(address)

  // pairs to show in pair list
  const fetchedPairsList = useDataForList(allPairs)

  // all transactions with this token
  const {
    transactions,
    hasMore: hasMoreTxns,
    loadMore: loadMoreTxns,
    loading: loadingTxns,
    isDateRangeFiltered,
    setDateRange,
    setDirection,
    clearDateRange,
  } = useTokenTransactions(address)

  // price
  const price = priceUSD ? formattedNum(priceUSD, true) : ''
  const priceChange = priceChangeUSD ? formattedPercent(priceChangeUSD) : ''

  // volume
  const volume =
    oneDayVolumeUSD || oneDayVolumeUSD === 0
      ? formattedNum(oneDayVolumeUSD === 0 ? oneDayVolumeUT : oneDayVolumeUSD, true)
      : oneDayVolumeUSD === 0
        ? '$0'
        : '-'

  // mark if using untracked volume
  const [usingUtVolume, setUsingUtVolume] = useState(false)
  useEffect(() => {
    setUsingUtVolume(oneDayVolumeUSD === 0 ? true : false)
  }, [oneDayVolumeUSD])

  const volumeChange = formattedPercent(!usingUtVolume ? volumeChangeUSD : volumeChangeUT)

  // liquidity
  const liquidity = totalLiquidityUSD ? formattedNum(totalLiquidityUSD, true) : totalLiquidityUSD === 0 ? '$0' : '-'
  const liquidityChange = formattedPercent(liquidityChangeUSD)

  // transactions
  const txnChangeFormatted = formattedPercent(txnChange)

  const below1080 = useMedia('(max-width: 1080px)')
  const below800 = useMedia('(max-width: 800px)')
  const below500 = useMedia('(max-width: 500px)')

  const formattedSymbol = replaceSymbolE({ id: address.toLowerCase(), symbol })

  const [dismissed, markAsDismissed] = usePathDismissed(history.location.pathname)
  const [savedTokens, addToken] = useSavedTokens()
  const listedTokens = useListedTokens()

  const showWarning = !dismissed && listedTokens && !listedTokens.includes(address) &&
    !(ENERGISWAP_DEFAULT_LIST.tokens.find((item) => item.address.toLowerCase() === address.toLowerCase()))

  useEffect(() => {
    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    })
  }, [])

  if (!isEnergiCoin) {
    tokensDesc = (
      <AutoRow align="flex-end" style={{ width: 'fit-content' }}>
        <TYPE.body>
          <BasicLink to="/tokens">{'Tokens '}</BasicLink>→ {replaceSymbolE({ id, symbol })}
          {'  '}
        </TYPE.body>
        <Link
          style={{ width: 'fit-content' }}
          color={backgroundColor}
          external
          href={EXPLORER_URL + '/address/' + address}
        >
          <Text style={{ marginLeft: '.15rem' }} fontSize={'14px'} fontWeight={400} color={'#34B7F1'}>
            ({address.slice(0, 8) + '...' + address.slice(36, 42)})
          </Text>
        </Link>
      </AutoRow>
    )
    addLiquidity = (
      <Link href={getPoolLink(address)} target="_blank" rel="noopener noreferrer">
        <ButtonNoBackground>+ Add Liquidity</ButtonNoBackground>
      </Link>
    )
    tokenInformation = (
      <>
        <RowBetween style={{ marginTop: '3rem' }}>
          <TYPE.main fontSize={'1.125rem'}>Token Information</TYPE.main>{' '}
        </RowBetween>
        <Panel
          rounded
          style={{
            marginTop: '1.5rem',
          }}
          p={20}
        >
          <TokenDetailsLayout>
            <Column>
              <TYPE.main>Symbol</TYPE.main>
              <Text style={{ marginTop: '.5rem' }} fontSize={24} fontWeight="500">
                <FormattedName text={replaceSymbolE({ id: address, symbol })} maxCharacters={12} />
              </Text>
            </Column>
            <Column>
              <TYPE.main>Name</TYPE.main>
              <TYPE.main style={{ marginTop: '.5rem' }} fontSize={24} fontWeight="500">
                <FormattedName text={replaceSyntheticName({ id: address, name })} maxCharacters={16} />
              </TYPE.main>
            </Column>
            <Column>
              <TYPE.main>Address</TYPE.main>
              <AutoRow align="flex-end">
                <TYPE.main style={{ marginTop: '.5rem' }} fontSize={24} fontWeight="500">
                  {address.slice(0, 8) + '...' + address.slice(36, 42)}
                </TYPE.main>
                <CopyHelper toCopy={address} />
              </AutoRow>
            </Column>
            <Link external href={EXPLORER_URL + '/address/' + address}>
              <ButtonNoBackground>
                View on Block Explorer ↗
              </ButtonNoBackground>
            </Link>
          </TokenDetailsLayout>
        </Panel>
      </>
    )
  } else {
    tokensDesc = (
      <AutoRow align="flex-end" style={{ width: 'fit-content' }}>
        <TYPE.body>
          <BasicLink to="/tokens">{'Tokens '}</BasicLink>→ Energi Coin
        </TYPE.body>
      </AutoRow>
    )
  }

  return (
    <PageWrapper>
      <Warning
        type={'token'}
        show={showWarning}
        setShow={markAsDismissed}
        address={address}
      />
      <ContentWrapper>
        <ScrollUp />
        <RowBetween style={{ flexWrap: 'wrap', alingItems: 'start' }}>
          {tokensDesc}
        </RowBetween>

        <WarningGrouping disabled={showWarning}>
          <DashboardWrapper style={{ marginTop: below1080 ? '0' : '1rem' }}>
            <RowBetween
              style={{
                flexWrap: 'wrap',
                marginBottom: '2rem',
                alignItems: 'flex-start',
              }}
            >
              <RowFixed style={{ flexWrap: 'wrap' }}>
                <RowFixed style={{ alignItems: 'baseline' }}>
                  <TokenLogo address={address} size="32px" style={{ alignSelf: 'center' }} />
                  <TYPE.main fontSize={below1080 ? '1.5rem' : '2rem'} fontWeight={500} style={{ margin: '0 1rem' }}>
                    <RowFixed gap="6px">
                      <FormattedName text={isEnergiCoin ? 'Energi' : (name ? replaceSyntheticName({ id: address, name }) + ' ' : '')} maxCharacters={16} style={{ marginRight: '6px' }} />{' '}
                      {isEnergiCoin ? '(NRG)' : (formattedSymbol ? `(${formattedSymbol})` : '')}
                    </RowFixed>
                  </TYPE.main>{' '}
                  {!below1080 && (
                    <>
                      <TYPE.main fontSize={'1.5rem'} fontWeight={500} style={{ marginRight: '1rem' }}>
                        {price}
                      </TYPE.main>
                      {priceChange}
                    </>
                  )}
                </RowFixed>
              </RowFixed>
              <span>
                <RowFixed ml={below500 ? '0' : '2.5rem'} mt={below500 ? '1rem' : '0'}>
                  {!!!savedTokens[address] && !below800 ? (
                    <Hover onClick={() => addToken(address, symbol)}>
                      <StyledIcon title="Pin Token">
                        <PlusCircle style={{ marginRight: '0.5rem' }} />
                      </StyledIcon>
                    </Hover>
                  ) : !below1080 ? (
                    <StyledIcon>
                      <Bookmark style={{ marginRight: '0.5rem', opacity: 0.4 }} />
                    </StyledIcon>
                  ) : (
                    <></>
                  )}
                  {addLiquidity}
                  <Link href={getSwapLink(!isEnergiCoin && address)} target="_blank" rel="noopener noreferrer">
                    <ButtonInverse ml={'.5rem'} mr={below1080 && '.5rem'}>Trade</ButtonInverse>
                  </Link>
                </RowFixed>
              </span>
            </RowBetween>

            <>
              <PanelWrapper style={{ marginTop: below1080 ? '0' : '1rem' }}>
                {below1080 && price && (
                  <Panel>
                    <AutoColumn gap="20px">
                      <RowBetween>
                        <TYPE.main>Price</TYPE.main>
                        <div />
                      </RowBetween>
                      <RowBetween align="flex-end">
                        {' '}
                        <TYPE.main fontSize={'1.5rem'} lineHeight={1} fontWeight={500}>
                          {price}
                        </TYPE.main>
                        <TYPE.main>{priceChange}</TYPE.main>
                      </RowBetween>
                    </AutoColumn>
                  </Panel>
                )}
                <Panel>
                  <AutoColumn gap="20px">
                    <RowBetween>
                      <TYPE.main>Total Liquidity</TYPE.main>
                      <div />
                    </RowBetween>
                    <RowBetween align="flex-end">
                      <TYPE.main fontSize={'1.5rem'} lineHeight={1} fontWeight={500}>
                        {liquidity}
                      </TYPE.main>
                      <TYPE.main>{liquidityChange}</TYPE.main>
                    </RowBetween>
                  </AutoColumn>
                </Panel>
                <Panel>
                  <AutoColumn gap="20px">
                    <RowBetween>
                      <TYPE.main>Volume (24hrs) {usingUtVolume && '(Untracked)'}</TYPE.main>
                      <div />
                    </RowBetween>
                    <RowBetween align="flex-end">
                      <TYPE.main fontSize={'1.5rem'} lineHeight={1} fontWeight={500}>
                        {volume}
                      </TYPE.main>
                      <TYPE.main>{volumeChange}</TYPE.main>
                    </RowBetween>
                  </AutoColumn>
                </Panel>

                <Panel>
                  <AutoColumn gap="20px">
                    <RowBetween>
                      <TYPE.main>Transactions (24hrs)</TYPE.main>
                      <div />
                    </RowBetween>
                    <RowBetween align="flex-end">
                      <TYPE.main fontSize={'1.5rem'} lineHeight={1} fontWeight={500}>
                        {oneDayTxns ? localNumber(oneDayTxns) : oneDayTxns === 0 ? 0 : '-'}
                      </TYPE.main>
                      <TYPE.main>{txnChangeFormatted}</TYPE.main>
                    </RowBetween>
                  </AutoColumn>
                </Panel>
                <Panel
                  style={{
                    gridColumn: below1080 ? '1' : '2/4',
                    gridRow: below1080 ? '' : '1/4',
                  }}
                >
                  <TokenChart address={address} color={backgroundColor} base={priceUSD} />
                </Panel>
              </PanelWrapper>
            </>

            <span>
              <TYPE.main fontSize={'1.125rem'} style={{ marginTop: '3rem' }}>
                Top Pairs
              </TYPE.main>
            </span>
            <Panel
              rounded
              style={{
                marginTop: '1.5rem',
                padding: '1.125rem 0 ',
              }}
            >
              {address && fetchedPairsList ? (
                <PairList color={backgroundColor} address={address} pairs={fetchedPairsList} />
              ) : (
                <Loader />
              )}
            </Panel>
            <RowBetween mt={40} mb={'1rem'}>
              <TYPE.main fontSize={'1.125rem'}>Transactions</TYPE.main> <div />
            </RowBetween>
            <Panel rounded>
              <TxnList
                color={'#34B7F1'}
                transactions={transactions}
                feedKey={address}
                hasMore={hasMoreTxns}
                onLoadMore={loadMoreTxns}
                isLoadingInitial={loadingTxns}
                isDateRangeFiltered={isDateRangeFiltered}
                onSetDateRange={setDateRange}
                onSetDirection={setDirection}
                onClearDateRange={clearDateRange}
              />
            </Panel>
            {tokenInformation}
          </DashboardWrapper>
        </WarningGrouping>
      </ContentWrapper>
    </PageWrapper>
  )
}

export default withRouter(TokenPage)
